import { skipToken } from '@reduxjs/toolkit/query';
import Sidebar from '@rio-cloud/rio-uikit/Sidebar';
import Button from 'components/actions/Button';
import DateTimeOrNoData from 'components/content/DateTimeOrNoData';
import NoData from 'components/content/NoData';
import { useDispatch, useSelector } from 'react-redux';
import { useGetSystemTeamsAttributesQuery } from 'services/systemAdmin/teams';
import {
    ActiveSidebar,
    clearActiveElements,
    getActiveSidebar,
    getSelectedTeam,
    setTeamDialog,
} from 'store/slices/settingsPageSlice';

import Can from '../../../components/auth/Can';
import type { SectionItem } from '../SidebarSection';

import type { SystemTeam } from 'API';
import './_styles.scss';

import SystemTeamsSidebarContent from './SystemTeamsSidebarContent';

interface FooterProps {
    onChange: () => void;
}

const Footer = ({ onChange }: FooterProps) => (
    <Can type='teams' action='edit'>
        <div className='Sidebar__footer SystemTeamsSidebar__footer'>
            <Button variant='outline' style='primary' className='Sidebar__button' onClick={onChange}>
                <span className='rioglyph rioglyph-pencil mr-5' />
                Edit
            </Button>
            <Button variant='outline' style='danger' className='Sidebar__button--cancel hidden'>
                <span className='rioglyph rioglyph-off -mr-2' />
            </Button>
        </div>
    </Can>
);

const SystemTeamsSidebar = () => {
    const dispatch = useDispatch();
    const activeSidebar = useSelector(getActiveSidebar);
    const selectedSystemTeam = useSelector(getSelectedTeam);

    const {
        data,
        isLoading: isLoadingSystemTeamAttributes,
        isFetching,
    } = useGetSystemTeamsAttributesQuery(selectedSystemTeam?.id ?? skipToken, {
        refetchOnMountOrArgChange: true,
    });

    const teamInfo: SectionItem[] = [
        {
            label: 'Created',
            element: <DateTimeOrNoData value={selectedSystemTeam?.createdAt} separator />,
        },
        {
            label: 'ID',
            element: selectedSystemTeam?.id ?? <NoData text='No data' className='text-14' />,
        },
    ];

    const openTeamDialog = (team: SystemTeam | undefined) => {
        dispatch(setTeamDialog({ team, showTeamDialog: true }));
    };

    const closeSidebar = () => {
        dispatch(clearActiveElements());
    };

    return (
        <Sidebar
            fly
            className='SystemTeamsSidebar__drawer'
            title={<p className='Sidebar__header'>Team detail</p>}
            titleClassName='pl-10'
            closed={activeSidebar !== ActiveSidebar.TEAMS}
            footer={<Footer onChange={() => openTeamDialog(selectedSystemTeam)} />}
            onClose={closeSidebar}
        >
            <SystemTeamsSidebarContent
                name={selectedSystemTeam?.name}
                numberOfUsers={selectedSystemTeam?.numberOfUsers}
                numberOfFleets={selectedSystemTeam?.numberOfFleets}
                users={data?.users?.map(user => `${user.firstName} ${user.lastName}`) ?? []}
                fleets={data?.fleets?.map(fleet => fleet.name) ?? []}
                teamInfo={teamInfo}
                isLoading={isLoadingSystemTeamAttributes || isFetching}
            />
        </Sidebar>
    );
};

export default SystemTeamsSidebar;
